/**
 * Intent Matcher — 키워드 기반 매칭 엔진
 *
 * Phase 1은 Rule 기반이므로 임베딩이나 LLM을 사용하지 않습니다.
 * 단순 키워드 포함 여부 + 빈도 가중치로 점수를 계산합니다.
 *
 * 설계 원칙 (CLAUDE.md):
 * - 인증 상태에 맞는 Q&A만 매칭 후보에 포함
 * - 임계값 미달 시 Fallback (임의 답변 생성 금지)
 *
 * 자세한 설계 의도는 docs/03-architecture.md 참조
 */

import type { QAEntry, MatchResult } from '@/types/qa';
import type { AuthStatus } from '@/types/api';

/** 매칭 최소 점수. 이 미만이면 Fallback */
export const MIN_MATCH_SCORE = 1.0;

/**
 * 텍스트 정규화: 소문자 + 공백 제거
 * 한국어 키워드는 공백을 다양하게 두는 경향이 있어 공백 제거가 효과적입니다.
 */
function normalize(text: string): string {
  return text.toLowerCase().replace(/\s+/g, '');
}

/**
 * 인증 상태에 따라 Q&A를 필터링
 */
function filterByAuth(qaList: QAEntry[], authStatus: AuthStatus): QAEntry[] {
  return qaList.filter((qa) => {
    if (qa.auth_required === 'public') return true;
    if (qa.auth_required === 'mixed') return true;
    // 'required' 는 인증 시에만
    return authStatus === 'authenticated';
  });
}

/**
 * 매칭 점수 계산
 *
 * 점수 구성:
 * - 매칭된 키워드 수 (기본 점수)
 * - 가장 긴 키워드의 길이 보너스 (긴 키워드일수록 변별력 높음)
 * - 빈도 가중치 (high: +0.5, medium: +0.2, low: 0)
 */
function calculateScore(
  matchedKeywords: string[],
  qa: QAEntry
): number {
  if (matchedKeywords.length === 0) return 0;

  let score = matchedKeywords.length;

  // 가장 긴 키워드 길이 보너스 (최대 +1.0)
  const longestKeyword = matchedKeywords.reduce(
    (max, kw) => Math.max(max, kw.length),
    0
  );
  if (longestKeyword >= 6) score += 1.0;
  else if (longestKeyword >= 4) score += 0.5;

  // 빈도 가중치
  if (qa.frequency === 'high') score += 0.5;
  else if (qa.frequency === 'medium') score += 0.2;

  return score;
}

/**
 * 메인 매칭 함수
 *
 * @param userText 사용자가 입력한 텍스트
 * @param qaDatabase 전체 Q&A 데이터
 * @param authStatus 현재 인증 상태
 * @returns 매칭 결과 (qa가 null이면 Fallback)
 */
export function matchIntent(
  userText: string,
  qaDatabase: QAEntry[],
  authStatus: AuthStatus
): MatchResult {
  if (!userText || userText.trim().length === 0) {
    return { qa: null, score: 0, matchedKeywords: [] };
  }

  const normalizedInput = normalize(userText);
  const candidates = filterByAuth(qaDatabase, authStatus);

  let best: MatchResult = { qa: null, score: 0, matchedKeywords: [] };

  for (const qa of candidates) {
    const matched = qa.keywords.filter((kw) =>
      normalizedInput.includes(normalize(kw))
    );

    if (matched.length === 0) continue;

    const score = calculateScore(matched, qa);

    if (score > best.score) {
      best = { qa, score, matchedKeywords: matched };
    }
  }

  // 임계값 미달 시 Fallback
  if (best.score < MIN_MATCH_SCORE) {
    return { qa: null, score: 0, matchedKeywords: [] };
  }

  return best;
}

/**
 * intent 값으로 Q&A를 직접 찾기
 *
 * FAQ 메뉴처럼 intent가 이미 결정된 경우 사용한다. 키워드 매칭을 거치지 않으므로
 * 정확하고 빠르다 (라벨 재매칭 시 발생하는 Fallback 위험 회피).
 */
export function findByIntent(
  intent: string,
  qaDatabase: QAEntry[],
  authStatus: AuthStatus
): QAEntry | null {
  const candidates = filterByAuth(qaDatabase, authStatus);
  return candidates.find((qa) => qa.intent === intent) ?? null;
}

/**
 * 디버그용: 모든 매칭 가능한 후보를 점수 순으로 반환
 */
export function matchIntentDebug(
  userText: string,
  qaDatabase: QAEntry[],
  authStatus: AuthStatus
): MatchResult[] {
  const normalizedInput = normalize(userText);
  const candidates = filterByAuth(qaDatabase, authStatus);

  const results: MatchResult[] = [];

  for (const qa of candidates) {
    const matched = qa.keywords.filter((kw) =>
      normalizedInput.includes(normalize(kw))
    );
    if (matched.length > 0) {
      results.push({ qa, score: calculateScore(matched, qa), matchedKeywords: matched });
    }
  }

  return results.sort((a, b) => b.score - a.score);
}
